import { motion, AnimatePresence } from "framer-motion";
import { useEffect, useRef } from "react";
import type { Player } from "../../types/game";
import { useGameStore } from "../../store/gameStore";
import { useLanguage } from "../../i18n/LanguageContext";
import Button from "../ui/Button";
import InvestmentPanel from "../investment/InvestmentPanel";
import SpendPhase from "../spend/SpendPhase";
import CardFace from "../cards/CardFace";

interface Props { player: Player; }

const pI = { opacity: 0, y: 8 };
const pA = { opacity: 1, y: 0 };
const pE = { opacity: 0, y: -6 };
const pT = { duration: 0.2, type: "tween" as const };

const cI = { opacity: 0, scale: 0.88, y: 12 };
const cA = { opacity: 1, scale: 1, y: 0 };
const cE = { opacity: 0, scale: 0.92 };

export default function ActionArea({ player }: Props) {
  const { phase, activeEventCard, activeDrawnCard, dispatch } = useGameStore();
  const { t } = useLanguage();
  const bottomRef = useRef<HTMLDivElement>(null);

  // Scroll into view on small screens whenever content expands
  useEffect(() => {
    const isMobile = window.innerWidth < 1024;
    if (!isMobile) return;
    // Small delay so the layout animation has started before we scroll
    const id = setTimeout(() => {
      bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
    }, 80);
    return () => clearTimeout(id);
  }, [phase, activeEventCard]);

  return (
    <motion.div layout transition={{ layout: { duration: 0.3, ease: "easeInOut" } }}
      className="bg-slate-800/70 backdrop-blur-sm rounded-2xl border border-white/10 p-4 shadow-xl shadow-black/20">
      <AnimatePresence mode="wait">

        {/* ── DRAW EVENT ── */}
        {phase === "DRAW_EVENT" && (
          <motion.div key="draw-event" initial={pI} animate={pA} exit={pE} transition={pT}
            className="flex flex-col items-center gap-4">
            {activeEventCard ? (
              <>
                <motion.div className="w-full" initial={cI} animate={cA} exit={cE} transition={{ duration: 0.4 }}>
                  <CardFace card={activeEventCard} />
                </motion.div>
                <p className="text-white/60 text-sm text-center leading-relaxed px-2">
                  {(t.events?.[activeEventCard.id]?.conceptExplanation) ?? activeEventCard.conceptExplanation}
                </p>
                <Button onClick={() => dispatch({ type: "CONFIRM_EVENT" })} size="lg" className="w-full">
                  {t.action.okContinue}
                </Button>
              </>
            ) : (
              <>
                <motion.div className="text-6xl" animate={{ scale: [1, 1.1, 1], rotate: [-5, 5, -5] }} transition={{ repeat: Infinity, duration: 2 }}>
                  ⚡
                </motion.div>
                <div className="text-white font-display font-bold text-xl text-center">{t.action.timeForEvent}</div>
                <p className="text-white/50 text-sm text-center max-w-xs">{t.action.eventDesc}</p>
                <Button onClick={() => dispatch({ type: "DRAW_EVENT_CARD" })} size="lg" className="w-full">
                  {t.action.drawEventCard}
                </Button>
              </>
            )}
          </motion.div>
        )}

        {/* ── CHOOSE DECK ── */}
        {phase === "CHOOSE_DECK" && (
          <motion.div key="choose-deck" initial={pI} animate={pA} exit={pE} transition={pT} className="space-y-3">
            <div className="text-center mb-1">
              <div className="font-display font-bold text-white text-xl mb-0.5">{t.action.choosePath}</div>
              <div className="text-white/40 text-sm">{t.action.whatWillYouDo}</div>
            </div>

            <motion.button
              whileHover={{ scale: 1.01, x: 2 }} whileTap={{ scale: 0.98 }}
              onClick={() => dispatch({ type: "CHOOSE_DECK", deck: "earnings" })}
              className="w-full bg-blue-500/10 hover:bg-blue-500/20 border border-blue-500/30 hover:border-blue-500/60 rounded-xl p-4 text-left transition-all group"
            >
              <div className="font-display font-bold text-blue-400 text-base group-hover:text-blue-300 transition-colors">{t.action.earningsDeck}</div>
              <div className="text-white/50 text-sm mt-0.5">{t.action.earningsDesc}</div>
            </motion.button>

            <motion.button
              whileHover={{ scale: 1.01, x: 2 }} whileTap={{ scale: 0.98 }}
              onClick={() => dispatch({ type: "CHOOSE_DECK", deck: "preparation" })}
              className="w-full bg-purple-500/10 hover:bg-purple-500/20 border border-purple-500/30 hover:border-purple-500/60 rounded-xl p-4 text-left transition-all group"
            >
              <div className="font-display font-bold text-purple-400 text-base group-hover:text-purple-300 transition-colors">{t.action.prepDeck}</div>
              <div className="text-white/50 text-sm mt-0.5">{t.action.prepDesc}</div>
            </motion.button>

            <motion.button
              whileHover={{ scale: 1.01, x: 2 }} whileTap={{ scale: 0.98 }}
              onClick={() => dispatch({ type: "REST" })}
              className="w-full bg-white/5 hover:bg-white/8 border border-white/10 hover:border-white/20 rounded-xl p-3 text-left transition-all"
            >
              <div className="font-display font-semibold text-white/60 text-sm">{t.action.rest}</div>
              <div className="text-white/30 text-xs mt-0.5">{t.action.restDesc}</div>
            </motion.button>
          </motion.div>
        )}

        {/* ── REVEAL CARD ── */}
        {phase === "REVEAL_CARD" && activeDrawnCard && (
          <motion.div key="reveal-card" initial={pI} animate={pA} exit={pE} transition={pT} className="space-y-3">
            <motion.div className="w-full" initial={cI} animate={cA} exit={cE} transition={{ duration: 0.4 }}>
              <CardFace card={activeDrawnCard} />
            </motion.div>
            <Button onClick={() => dispatch({ type: "CONFIRM_CARD" })} size="lg" className="w-full">{t.action.nextInvest}</Button>
          </motion.div>
        )}

        {/* ── INVEST ── */}
        {phase === "INVEST" && (
          <motion.div key="invest" initial={pI} animate={pA} exit={pE} transition={pT}>
            <div className="font-display font-bold text-white text-base mb-3 text-center">{t.action.investments}</div>
            <InvestmentPanel player={player} />
          </motion.div>
        )}

        {/* ── SPEND / BUY_ITEMS ── */}
        {(phase === "SPEND" || phase === "BUY_ITEMS") && (
          <motion.div key="spend" initial={pI} animate={pA} exit={pE} transition={pT}>
            <SpendPhase player={player} />
          </motion.div>
        )}

        {/* ── END_TURN (burnout) ── */}
        {phase === "END_TURN" && (
          <motion.div key="end-turn" initial={pI} animate={pA} exit={pE} transition={pT}
            className="flex flex-col items-center gap-4 py-4">
            <motion.div className="text-5xl" animate={{ rotate: [-10, 10, -10] }} transition={{ repeat: Infinity, duration: 1.2 }}>
              💤
            </motion.div>
            <div className="text-white font-display font-bold text-lg text-center">
              {player.name} {t.action.burnoutTitle}
            </div>
            <div className="text-white/50 text-sm text-center max-w-xs">{t.action.burnoutDesc}</div>
            <Button onClick={() => dispatch({ type: "END_TURN" })} size="lg" className="w-full">{t.action.recoverPassTurn}</Button>
          </motion.div>
        )}

      </AnimatePresence>
      <div ref={bottomRef} />
    </motion.div>
  );
}
